import styled, { css, keyframes } from "styled-components";

const shimmer = keyframes`
	0% { background-position: -500px 0; }
	100% { background-position: 500px 0; }
`;

const finishTask = keyframes`
	0% { opacity: 1; transform: translateY(0); max-height: 120px; }
	70% { opacity: 0; transform: translateY(-6px); max-height: 120px; }
	100% { opacity: 0; transform: translateY(-6px); max-height: 0; padding-top: 0; padding-bottom: 0; margin: 0; border-width: 0; }
`;

export const Container = styled.section`
	background: #ffffff;
	border: 1px solid #dcdfe6;
	border-radius: 10px;
	padding: 21px 21px 18px;
	width: 100%;
	display: flex;
	flex-direction: column;
	margin-top: 24px;
`;

export const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	margin-bottom: 20px;
`;

export const Title = styled.h2`
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 16px;
	font-weight: 700;
	color: #000e4b;
	margin: 0;
	display: flex;
	align-items: center;
	gap: 8px;
`;

export const BadgeSoon = styled.span`
	background: #e0f2fe;
	color: #0369a1;
	border-radius: 100px;
	padding: 4px 8px;
	font-size: 11px;
	font-weight: 800;
	font-family: "NotoSans-Bold", sans-serif;
	line-height: 1;
`;

export const BadgeCount = styled.span`
	background: #fafafa;
	border: 1px solid #dedfe3;
	color: #797d8d;
	border-radius: 100px;
	padding: 4px 10px;
	font-size: 12px;
	font-weight: 600;
	font-family: "NotoSans-SemiBold", sans-serif;
	line-height: 1;
`;

export const EmptyStateContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
	text-align: center;
`;

export const EmptyTitle = styled.h3`
	font-family: "NotoSans-Regular";
	font-size: 20px;
	color: #3d4259;
	margin: 16px 0 8px;
	font-weight: bold;
`;

export const EmptyDesc = styled.p`
	font-family: "NotoSans-Regular", sans-serif;
	font-size: 16px;
	color: #000e4b50;
	margin: 0;
	max-width: 320px;
	line-height: 1.4;
`;

export const TaskListContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 100%;
`;

export const TaskCard = styled.article<{
	completion?: "success" | "dismissed" | null;
}>`
	background: #fafafa;
	border: 1px solid #dedfe3;
	border-radius: 16px;
	padding: 14px 18px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	transition: border-color 0.2s, box-shadow 0.2s;
	${({ completion }) =>
		completion
			? css`
					animation: ${finishTask} 400ms ease-in forwards;
					pointer-events: none;
			  `
			: undefined}
	${({ completion }) =>
		completion === "success" ? "border-color: #00a376;" : ""}

	&:hover {
		border-color: #c4c6cc;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
	}

	@media (max-width: 620px) {
		flex-direction: column;
		align-items: flex-start;
		gap: 16px;
	}
`;

export const TaskLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex: 1;
	width: 100%;
`;

export const TaskInfo = styled.div`
	display: flex;
	flex-direction: column;
	gap: 3px;
`;

export const TaskPatientName = styled.h4`
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 15px;
	font-weight: 700;
	color: #000e4b;
	margin: 0;
`;

export const TaskDesc = styled.span`
	font-family: "NotoSans-Medium", sans-serif;
	font-size: 13px;
	font-weight: 500;
	color: #797d8d;
`;

export const TaskRight = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-shrink: 0;

	@media (max-width: 620px) {
		width: 100%;
		justify-content: space-between;
	}
`;

export const TaskActionButton = styled.button`
	border: 1px solid #dedfe3;
	background: #ffffff;
	border-radius: 8px;
	padding: 8px 14px;
	font-family: "NotoSans-SemiBold", sans-serif;
	font-size: 12px;
	font-weight: 600;
	color: #111827;
	cursor: pointer;
	transition: background-color 0.2s, border-color 0.2s;

	&:hover {
		background-color: #f3f4f6;
		border-color: #d1d5db;
	}

	&:disabled {
		opacity: 0.65;
		cursor: wait;
	}
`;

export const TrashButton = styled.button`
	background: transparent;
	border: 0;
	padding: 4px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #797d8d;
	transition: color 0.2s;

	&:hover {
		color: #ef4444;
	}

	&:disabled {
		opacity: 0.45;
		cursor: wait;
	}
`;

export const TaskError = styled.span`
	font-family: "NotoSans-Regular", sans-serif;
	font-size: 12px;
	color: #d13e3e;
	line-height: 1.35;
`;

export const FocusError = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 18px;
	color: #d13e3e;
	font-family: "NotoSans-Regular", sans-serif;
	font-size: 13px;

	button {
		border: 0;
		background: transparent;
		color: #006b57;
		font-family: "NotoSans-Bold", sans-serif;
		text-decoration: underline;
		cursor: pointer;
	}
`;

export const SkeletonCard = styled.div`
	height: 76px;
	border-radius: 16px;
	background: linear-gradient(90deg, #f2f3f5 25%, #e4e7eb 50%, #f2f3f5 75%);
	background-size: 700px 100%;
	animation: ${shimmer} 1.3s infinite linear;
`;

export const RefreshingText = styled.span`
	font-family: "NotoSans-Regular", sans-serif;
	font-size: 11px;
	color: #797d8d;
	margin: -10px 0 10px;
	text-align: right;
`;
